import { createInertiaApp, router } from '@inertiajs/react'
import '../application.css'

// Product analytics (#600). Config comes from <meta> in the layout rather
// than from import.meta.env, because the Docker image is built once and
// deployed to staging and production both: a build-time token would report
// staging's traffic into production's project. No token means analytics is
// off server side, so it stays off here too.
function meta(name: string): string | undefined {
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content || undefined
}

type Page = { url: string; props?: { auth?: { user?: { id: string } | null } } }

// The initial page is not a visit, so Inertia fires no navigate event for it.
// It arrives as JSON in the element inertia_rails renders
// (use_script_element_for_initial_page).
function initialPage(): Page | null {
  const el = document.querySelector('script[data-page]')
  try {
    return el?.textContent ? (JSON.parse(el.textContent) as Page) : null
  } catch {
    return null
  }
}

type PostHog = typeof import('posthog-js').default

// Replay masks every text node, so text is readable only inside a container
// the app has marked as carrying no customer data. Marketing pages opt in;
// everything behind the login does not.
const UNMASKED_SELECTOR = '[data-ph-unmask]'

// rrweb calls this for each text node it is about to mask. Anything
// unexpected, a detached node included, falls through to masked.
function maskText(text: string, element?: HTMLElement): string {
  try {
    if (element?.closest(UNMASKED_SELECTOR)) return text
  } catch {
    // fall through to masked
  }
  // Same shape as rrweb's own masking: whitespace survives, so the replay
  // keeps its line breaks and word lengths instead of one solid block.
  return text.replace(/\S/g, '*')
}

// rrweb records attributes verbatim and maskTextSelector never touches them,
// so the contacts table shipped its addresses out in aria-label (#670).
// Attributes are default-deny too: presentation and layout survive, the rest
// is masked. maskAllElementAttributes would cover this but takes class, style
// and src with it, which leaves the replay unstyled.
const RECORDABLE_ATTRIBUTES = new Set([
  // layout and presentation
  'class', 'style', 'id', 'dir', 'lang', 'hidden', 'tabindex', 'role', 'target',
  'colspan', 'rowspan', 'type', 'disabled', 'checked', 'selected', 'open',
  // media the replay has to re-fetch to render
  'src', 'srcset', 'sizes', 'href', 'rel', 'loading', 'width', 'height',
  // svg geometry, without which icons collapse
  'viewbox', 'preserveaspectratio', 'd', 'fill', 'stroke', 'stroke-width',
  'transform', 'points', 'x', 'y', 'cx', 'cy', 'r', 'xmlns',
  // aria state, which is structure rather than content
  'aria-hidden', 'aria-expanded', 'aria-selected', 'aria-current',
])

function maskAttribute(name: string, value: string, element?: Element): string {
  if (RECORDABLE_ATTRIBUTES.has(name.toLowerCase())) return value
  try {
    if (element?.closest(UNMASKED_SELECTOR)) return value
  } catch {
    // fall through to masked
  }
  return value.replace(/\S/g, '*')
}

function startAnalytics(posthog: PostHog, token: string) {
  posthog.init(token, {
    // Our own subdomain, CNAME'd onto PostHog's managed proxy, so ad blockers
    // do not eat the events. ui_host has to be spelled out alongside it or the
    // toolbar and the "view in PostHog" links point at the proxy, which only
    // carries ingestion.
    api_host: meta('posthog-host'),
    ui_host: meta('posthog-ui-host'),
    // Inertia never reloads the document, so posthog-js' own pageview
    // tracking would fire once on first paint and never again. Sent from the
    // router below instead.
    capture_pageview: false,
    // Replay records the DOM, and the app renders recipient addresses, contact
    // lists and campaign bodies belonging to our customers' contacts. So it is
    // default-deny: every text node and input is masked, and only what the app
    // marks as free of customer data is recorded as written (#666, #115).
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskTextFn: maskText,
      maskAttributeFn: maskAttribute,
      // Every iframe in this app is an email preview rendering a customer's
      // campaign HTML. Block the element rather than masking inside it.
      blockSelector: 'iframe',
      // Request and response bodies are contact records in JSON.
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
    },
    // We do not use in-app surveys. Preventing the optional surveys bundle
    // from loading keeps it out of the initial-page network waterfall.
    disable_surveys: true,
    // Clicks, and how far down a page someone got before leaving. Without
    // these the marketing site could report that a visitor loaded the
    // homepage and nothing else: no idea whether they read past the hero or
    // which call to action they ignored, which is most of what we want to
    // know about the 25 people a week who land there and leave (#679).
    //
    // Set explicitly rather than left to the defaults, because the default is
    // remote config and this should not change under us.
    //
    // Replay masking does not reach autocapture: each event carries the
    // clicked element's text, so a click on a contact row sent that contact's
    // name to PostHog (#668). Autocapture is default-deny the same way replay
    // is, limited to containers marked [data-ph-unmask]. The allowlist matches
    // the click target or any parent, and rage clicks and copy events go
    // through the same check. Inside the app, the named events in lib/track.ts
    // and the server-side captures are what count.
    autocapture: {
      css_selector_allowlist: [UNMASKED_SELECTOR],
    },
    // Dead clicks skip the autocapture allowlist entirely and send element
    // text too, so they stay off rather than arriving by remote config.
    capture_dead_clicks: false,
    capture_pageleave: true,
  })

  let identified: string | null = null
  let lastUrl: string | null = null

  const sync = (page: Page | null) => {
    if (!page) return

    const user = page.props?.auth?.user ?? null

    if (user && user.id !== identified) {
      // Ties the anonymous visitor posthog-js has been collecting against to
      // the account, which is what makes "landed on pricing, then signed up"
      // one person rather than two.
      posthog.identify(user.id)
      identified = user.id
    } else if (!user && identified) {
      // Signing out must not leave the next person on this browser attached
      // to the previous account.
      posthog.reset()
      identified = null
    }

    // navigate also fires for back/forward, where the URL can be unchanged.
    // Guarding on it keeps history restores out of the pageview count.
    if (page.url !== lastUrl) {
      lastUrl = page.url
      posthog.capture('$pageview')
    }
  }

  sync(initialPage())
  router.on('navigate', (event) => sync(event.detail.page as Page))
}

const token = meta('posthog-token')
if (token) {
  // Analytics is useful, but it is not required to render or operate the
  // product. Keeping the SDK in a dynamic chunk removes it from every
  // visitor's critical JavaScript path, including the public marketing pages.
  const loadAnalytics = () => {
    void import('posthog-js').then(({ default: posthog }) => startAnalytics(posthog, token))
  }

  if (document.readyState === 'complete') {
    window.setTimeout(loadAnalytics, 0)
  } else {
    window.addEventListener('load', loadAnalytics, { once: true })
  }
}

void createInertiaApp({
  pages: '../pages',
})
